import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApiMutation";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Mail, Send, Users, Eye, Trash2, Search, ArrowUpDown, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

export default function EmailCampaigns() {
  
  const queryClient = useQueryClient();
  const [showComposer, setShowComposer] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [searchEmail, setSearchEmail] = useState("");
  const [sortBy, setSortBy] = useState<"email" | "opened" | "clicked" | "bounced">("email");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [formData, setFormData] = useState({
    subject: "",
    htmlContent: "",
    fromEmail: "noreply@chefaiq.com",
    testEmail: "",
    template: "blank",
    audience: "all", // all, selected, subscription
    subscriptionPlanId: "",
    selectedUserIds: [] as string[],
  });

  const emailTemplates = {
    blank: "",
    announcement: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9fafb;">
        <div style="background-color: white; border-radius: 8px; padding: 40px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <h1 style="color: #1f2937; margin-bottom: 20px;">Important Announcement</h1>
          <p style="color: #4b5563; line-height: 1.6; margin-bottom: 20px;">
            We have an important update to share with you...
          </p>
          <p style="color: #4b5563; line-height: 1.6;">
            Best regards,<br/>
            The ChefAIQ Team
          </p>
        </div>
        <div style="text-align: center; margin-top: 20px; color: #9ca3af; font-size: 12px;">
          <p>© 2026 ChefAIQ. All rights reserved.</p>
        </div>
      </div>
    `,
    promotion: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #f9fafb;">
        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px; text-align: center;">
          <h1 style="color: white; margin: 0; font-size: 32px;">Special Offer!</h1>
        </div>
        <div style="background-color: white; padding: 40px;">
          <p style="color: #4b5563; line-height: 1.6; font-size: 16px;">
            We're excited to offer you an exclusive deal...
          </p>
          <div style="text-align: center; margin: 30px 0;">
            <a href="#" style="background-color: #667eea; color: white; padding: 15px 40px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold;">
              Claim Your Offer
            </a>
          </div>
          <p style="color: #9ca3af; font-size: 14px; text-align: center;">
            Offer expires soon. Don't miss out!
          </p>
        </div>
      </div>
    `,
    newsletter: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff;">
        <div style="background-color: #1f2937; padding: 20px; text-align: center;">
          <h2 style="color: white; margin: 0;">ChefAIQ Newsletter</h2>
        </div>
        <div style="padding: 40px;">
          <h3 style="color: #1f2937; margin-bottom: 15px;">What's New This Week</h3>
          <p style="color: #4b5563; line-height: 1.6;">
            Here are the latest updates and tips from ChefAIQ...
          </p>
          <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 30px 0;" />
          <h3 style="color: #1f2937; margin-bottom: 15px;">Featured Recipe</h3>
          <p style="color: #4b5563; line-height: 1.6;">
            Check out this amazing recipe...
          </p>
        </div>
        <div style="background-color: #f9fafb; padding: 20px; text-align: center; color: #9ca3af; font-size: 12px;">
          <p>You're receiving this because you're a valued ChefAIQ member.</p>
        </div>
      </div>
    `,
  };

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link", "image"],
      ["clean"],
    ],
  }), []);

  const { data: campaigns = [] } = useQuery({
    queryKey: ["/api/admin/email-campaigns"],
    queryFn: () => apiRequest("GET", "/api/admin/email-campaigns") as unknown as Promise<any[]>,
  });

  const { data: plans = [] } = useQuery({
    queryKey: ["/api/admin/subscription-plans"],
    queryFn: () => apiRequest("GET", "/api/admin/subscription-plans") as unknown as Promise<any[]>,
  });

  const sendMutation = useApiMutation({
    endpoint: "/api/admin/email-campaigns/send",
    method: "POST",
    invalidate: [["/api/admin/email-campaigns"]],
    successMessage: "Email campaign sent successfully",
    errorMessage: "Failed to send campaign",
    onSuccess: () => {
      setShowComposer(false);
      setFormData({ subject: "", htmlContent: "", fromEmail: "noreply@chefaiq.com", testEmail: "", template: "blank", audience: "all", subscriptionPlanId: "", selectedUserIds: [] });
    },
  });

  const deleteMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/admin/email-campaigns/${vars.id}`,
    method: "DELETE",
    invalidate: [["/api/admin/email-campaigns"]],
    successMessage: "Campaign deleted",
  });

  const handleSend = () => {
    if (!formData.subject || !formData.htmlContent) {
      showError("Subject and content are required");
      return;
    }
    sendMutation.mutate(formData);
  };

  const viewStats = async (campaign: any) => {
    const stats = await apiRequest("GET", `/api/admin/email-campaigns/${campaign._id}/stats`);
    setSelectedCampaign({ ...campaign, stats });
    setShowStats(true);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-xl sm:text-3xl font-bold">Email Campaigns</h1>
        <Button onClick={() => setShowComposer(true)}>
          <Mail className="h-4 w-4 mr-2" />
          Broadcast Email
        </Button>
      </div>

      <div className="grid gap-4">
        {campaigns.map((campaign: any) => (
          <Card key={campaign._id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>{campaign.subject}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sent to {campaign.recipientCount} users • {new Date(campaign.sentAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => viewStats(campaign)}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => deleteMutation.mutate({ id: campaign._id })}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-6 text-sm">
                <div>
                  <span className="text-muted-foreground">Opened:</span>{" "}
                  <span className="font-medium">{campaign.openedCount || 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Clicked:</span>{" "}
                  <span className="font-medium">{campaign.clickedCount || 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Bounced:</span>{" "}
                  <span className="font-medium">{campaign.bouncedCount || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showComposer} onOpenChange={setShowComposer}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Broadcast Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Test Email (send to specific email only)</Label>
              <Input
                value={formData.testEmail}
                onChange={(e) => setFormData({ ...formData, testEmail: e.target.value })}
                placeholder="nico@parazodigital.com"
                type="email"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave empty to send to all users. Fill to send test email only.
              </p>
            </div>

            <div>
              <Label>From Email</Label>
              <Select value={formData.fromEmail} onValueChange={(v) => setFormData({ ...formData, fromEmail: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="noreply@chefaiq.com">noreply@chefaiq.com</SelectItem>
                  <SelectItem value="support@chefaiq.com">support@chefaiq.com</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Test Email (Optional - send to specific email instead of all users)</Label>
              <Input
                value={formData.testEmail || ""}
                onChange={(e) => setFormData({ ...formData, testEmail: e.target.value })}
                placeholder="your@email.com"
                type="email"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave empty to send to selected audience. Fill to send test email only.
              </p>
            </div>

            <div>
              <Label>Audience</Label>
              <Select value={formData.audience} onValueChange={(v) => setFormData({ ...formData, audience: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="subscription">By Subscription Plan</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.audience === "subscription" && (
              <div>
                <Label>Subscription Plan</Label>
                <Select value={formData.subscriptionPlanId} onValueChange={(v) => setFormData({ ...formData, subscriptionPlanId: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((plan: any) => (
                      <SelectItem key={plan._id} value={plan._id}>{plan.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Subject</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Email subject"
              />
            </div>

            <div>
              <Label>Email Template</Label>
              <Select 
                value={formData.template} 
                onValueChange={(v: any) => {
                  setFormData({ 
                    ...formData, 
                    template: v,
                    htmlContent: emailTemplates[v as keyof typeof emailTemplates] || ""
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="blank">Blank</SelectItem>
                  <SelectItem value="announcement">Announcement</SelectItem>
                  <SelectItem value="promotion">Promotion</SelectItem>
                  <SelectItem value="newsletter">Newsletter</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Email Content</Label>
              <div className="border rounded-md">
                <ReactQuill
                  theme="snow"
                  value={formData.htmlContent}
                  onChange={(content) => setFormData({ ...formData, htmlContent: content })}
                  modules={quillModules}
                  style={{ minHeight: "300px" }}
                />
              </div>
            </div>

            <Button onClick={handleSend} disabled={sendMutation.isPending} className="w-full">
              <Send className="h-4 w-4 mr-2" />
              {sendMutation.isPending ? "Sending..." : "Send Campaign"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showStats} onOpenChange={setShowStats}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCampaign?.subject}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{selectedCampaign?.stats?.opened || 0}</div>
                  <div className="text-sm text-muted-foreground">Opened</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{selectedCampaign?.stats?.clicked || 0}</div>
                  <div className="text-sm text-muted-foreground">Clicked</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{selectedCampaign?.stats?.bounced || 0}</div>
                  <div className="text-sm text-muted-foreground">Bounced</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{selectedCampaign?.stats?.complained || 0}</div>
                  <div className="text-sm text-muted-foreground">Spam Reports</div>
                </CardContent>
              </Card>
            </div>

            {selectedCampaign?.stats?.recipients && (() => {
              const filtered = selectedCampaign.stats.recipients.filter((r: any) =>
                r.email.toLowerCase().includes(searchEmail.toLowerCase())
              );
              
              const sorted = [...filtered].sort((a: any, b: any) => {
                let aVal = sortBy === "email" ? a.email : a[sortBy];
                let bVal = sortBy === "email" ? b.email : b[sortBy];
                
                if (sortBy !== "email") {
                  aVal = aVal ? 1 : 0;
                  bVal = bVal ? 1 : 0;
                }
                
                if (sortOrder === "asc") {
                  return aVal > bVal ? 1 : -1;
                } else {
                  return aVal < bVal ? 1 : -1;
                }
              });
              
              return (
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold">Recipients ({filtered.length})</h3>
                    <div className="flex gap-2">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search email..."
                          value={searchEmail}
                          onChange={(e) => setSearchEmail(e.target.value)}
                          className="pl-8 w-64"
                        />
                      </div>
                      <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="opened">Opened</SelectItem>
                          <SelectItem value="clicked">Clicked</SelectItem>
                          <SelectItem value="bounced">Bounced</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                      >
                        <ArrowUpDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-3">Email</th>
                          <th className="text-center p-3">Sent</th>
                          <th className="text-center p-3">Opened</th>
                          <th className="text-center p-3">Clicked</th>
                          <th className="text-center p-3">Bounced</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map((r: any, i: number) => (
                          <tr key={i} className="border-t">
                            <td className="p-3">{r.email}</td>
                            <td className="text-center p-3">{r.sentAt ? '✓' : '-'}</td>
                            <td className="text-center p-3">{r.opened ? '✓' : '-'}</td>
                            <td className="text-center p-3">{r.clicked ? '✓' : '-'}</td>
                            <td className="text-center p-3">{r.bounced ? '✓' : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
